import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AssetNode, AssetEdge } from '@/types/asset-relationship';

export interface DiscoverySummary {
  networkCount: number;
  userCount: number;
  softwareCount: number;
  serviceCount: number;
  hotfixCount: number;
  lastSeenAt: string | null;
  // detail arrays for list view
  networks: { name: string; ip?: string; mac?: string; status?: string }[];
  users: { name: string; enabled?: boolean }[];
  software: { name: string; version?: string; vendor?: string }[];
  services: { name: string; status?: string }[];
  hotfixes: { id: string; installedOn?: string }[];
}

export function useDiscoveredItems(ativoId: string) {
  return useQuery({
    queryKey: ['discovered-items-summary', ativoId],
    queryFn: async (): Promise<DiscoverySummary | null> => {
      const [ativoRes, snapRes] = await Promise.all([
        supabase
          .from('cmdb_ativos')
          .select('software_instalado, ips, mac_addresses')
          .eq('id', ativoId)
          .single(),
        supabase
          .from('discovery_snapshots')
          .select('network_detail, local_users, services, hotfixes, collected_at')
          .eq('ativo_id', ativoId)
          .order('collected_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const ativo = ativoRes.data;
      const snap = snapRes.data;
      if (!ativo && !snap) return null;

      const swItems  = (ativo?.software_instalado as any[]) ?? [];
      const netItems = (snap?.network_detail   as any[]) ?? [];
      const usrItems = (snap?.local_users      as any[]) ?? [];
      const svcItems = (snap?.services         as any[]) ?? [];
      const hfxItems = (snap?.hotfixes         as any[]) ?? [];

      return {
        networkCount:  netItems.length  || ((ativo?.ips as any[]) ?? []).length,
        userCount:     usrItems.length,
        softwareCount: swItems.length,
        serviceCount:  svcItems.length,
        hotfixCount:   hfxItems.length,
        lastSeenAt:    snap?.collected_at ?? null,
        networks: netItems.map(n => ({
          name:   n.description || n.name || 'Interface',
          ip:     (n.ip_addresses as string[] | undefined)?.[0],
          mac:    n.mac_address,
          status: n.status,
        })),
        users: usrItems.map(u => ({ name: u.name || '?', enabled: u.enabled })),
        software: swItems.map(s => ({ name: s.name, version: s.version, vendor: s.vendor })),
        services: svcItems.map(s => ({ name: s.display_name || s.name || '?', status: s.status })),
        hotfixes: hfxItems.map(h => ({ id: h.hotfix_id || '?', installedOn: h.installed_on })),
      };
    },
    enabled: !!ativoId,
    staleTime: 120_000,
  });
}

/** Build virtual nodes + edges to inject into the CMDB graph */
export function buildDiscoveryGraphData(
  assetId: string,
  summary: DiscoverySummary,
): { nodes: AssetNode[]; edges: AssetEdge[] } {
  const nodes: AssetNode[] = [];
  const edges: AssetEdge[] = [];

  const add = (
    id: string,
    name: string,
    type: string,
    icon: string,
    count: number,
  ) => {
    if (count === 0) return;
    nodes.push({ id, name: `${name} (${count})`, type, icon, status: 'em_uso', importance: 'low' });
    edges.push({
      id: `disc-edge-${id}`,
      source: assetId,
      target: id,
      type: 'contains',
      importance: 'low',
      label: type,
    });
  };

  add(`disc-net-${assetId}`,  'Rede',           'Rede',           'Wifi',    summary.networkCount);
  add(`disc-usr-${assetId}`,  'Usuários',        'Usuário Local',  'Users',   summary.userCount);
  add(`disc-sw-${assetId}`,   'Software',        'Software',       'Package', summary.softwareCount);
  add(`disc-svc-${assetId}`,  'Serviços',        'Serviço',        'Monitor', summary.serviceCount);
  add(`disc-hfx-${assetId}`,  'Patches',         'Hotfix',         'Shield',  summary.hotfixCount);

  return { nodes, edges };
}
